import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  createBonusCampaign,
  fetchBonusCampaignTemplates,
  fetchBonusCampaigns,
  fetchBonusLedgers,
  fetchBonusSettings,
  fetchBonusSummary,
  grantBonus,
  previewBonusCampaign,
  reverseBonus,
  runBonusCampaign,
  updateBonusSettings,
} from '../../services/bonusManagement';
import { searchUsers } from '../../services/wallet';

const INITIAL_FORM = {
  user_id: '',
  action: 'grant',
  amount: '',
  note: '',
  source_reference: '',
  expires_at: '',
};

const INITIAL_CAMPAIGN = {
  name: '',
  campaign_type: 'custom',
  audience_type: 'selected_users',
  amount: '',
  expiry_days: 30,
  note: '',
  selected_users: [],
  start_date: '',
  end_date: '',
  inactive_days: 60,
  min_order_value: '',
};

const formatMoney = (value) => {
  const number = Number(value || 0);
  return `NGN ${number.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const actionColor = {
  grant: 'success',
  apply: 'warning',
  reverse: 'error',
  expire: 'default',
  adjust: 'info',
};

const buildAudienceFilters = (campaign) => ({
  user_ids: campaign.selected_users,
  start_date: campaign.start_date || undefined,
  end_date: campaign.end_date || undefined,
  inactive_days: campaign.inactive_days || undefined,
  min_order_value: campaign.min_order_value || undefined,
});

const BonusManagement = () => {
  const [summary, setSummary] = useState(null);
  const [settings, setSettings] = useState({ usage_percentage: '50' });
  const [ledger, setLedger] = useState([]);
  const [users, setUsers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [filters, setFilters] = useState({ search: '', action: '', start_date: '', end_date: '' });
  const [form, setForm] = useState(INITIAL_FORM);
  const [campaignForm, setCampaignForm] = useState(INITIAL_CAMPAIGN);
  const [campaignPreview, setCampaignPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const userOptions = useMemo(() => users.map((user) => ({
    id: user.sid || user.id,
    label: `${user.fullname || user.username || user.email || user.sid} (${user.email || user.phone || user.sid})`,
  })), [users]);

  const campaignPayload = useMemo(() => ({
    name: campaignForm.name,
    campaign_type: campaignForm.campaign_type,
    audience_type: campaignForm.audience_type,
    amount: campaignForm.amount,
    expiry_days: campaignForm.expiry_days,
    note: campaignForm.note,
    audience_filters: buildAudienceFilters(campaignForm),
  }), [campaignForm]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryResponse, settingsResponse, ledgerResponse, userResponse, campaignsResponse, templatesResponse] = await Promise.all([
        fetchBonusSummary({
          start_date: filters.start_date || undefined,
          end_date: filters.end_date || undefined,
        }),
        fetchBonusSettings(),
        fetchBonusLedgers({
          search: filters.search || undefined,
          action: filters.action || undefined,
          start_date: filters.start_date || undefined,
          end_date: filters.end_date || undefined,
          page_size: 50,
        }),
        searchUsers({ role: 'user' }),
        fetchBonusCampaigns({ page_size: 20 }),
        fetchBonusCampaignTemplates(),
      ]);
      setSummary(summaryResponse);
      setSettings({ usage_percentage: String(settingsResponse.usage_percentage ?? '50') });
      setLedger(ledgerResponse.results || []);
      setUsers(userResponse.results || []);
      setCampaigns(campaignsResponse.results || []);
      setTemplates(templatesResponse.results || []);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || error.message || 'Failed to load bonus data.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [filters.action, filters.end_date, filters.search, filters.start_date]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await updateBonusSettings({ usage_percentage: settings.usage_percentage });
      setSnackbar({ open: true, message: 'Bonus usage setting saved.', severity: 'success' });
      loadData();
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to save bonus setting.', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.user_id || !form.amount || !form.note.trim()) {
      setSnackbar({ open: true, message: 'Select user, amount, and reason.', severity: 'warning' });
      return;
    }
    if (form.action === 'grant' && !form.expires_at) {
      setSnackbar({ open: true, message: 'Expiry date is required for bonus grants.', severity: 'warning' });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        user_id: form.user_id,
        amount: form.amount,
        note: form.note.trim(),
        source_reference: form.source_reference.trim(),
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : undefined,
      };
      if (form.action === 'grant') {
        await grantBonus(payload);
      } else {
        await reverseBonus(payload);
      }
      setSnackbar({ open: true, message: 'Bonus action completed.', severity: 'success' });
      setForm(INITIAL_FORM);
      loadData();
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to complete bonus action.', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleTemplateChange = (event) => {
    const templateType = event.target.value;
    const template = templates.find((item) => item.campaign_type === templateType);
    setCampaignPreview(null);
    setCampaignForm((prev) => ({
      ...prev,
      campaign_type: templateType,
      name: template?.name || prev.name,
      audience_type: template?.audience_type || prev.audience_type,
      expiry_days: template?.expiry_days || prev.expiry_days,
    }));
  };

  const handlePreviewCampaign = async () => {
    if (!campaignForm.amount) {
      setSnackbar({ open: true, message: 'Enter campaign amount before preview.', severity: 'warning' });
      return;
    }
    setSaving(true);
    try {
      const preview = await previewBonusCampaign(campaignPayload);
      setCampaignPreview(preview);
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to preview campaign.', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleCreateCampaign = async () => {
    if (!campaignForm.name.trim() || !campaignForm.amount || !campaignForm.note.trim()) {
      setSnackbar({ open: true, message: 'Campaign name, amount, and reason are required.', severity: 'warning' });
      return;
    }
    setSaving(true);
    try {
      await createBonusCampaign(campaignPayload);
      setSnackbar({ open: true, message: 'Campaign created.', severity: 'success' });
      setCampaignForm(INITIAL_CAMPAIGN);
      setCampaignPreview(null);
      loadData();
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to create campaign.', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleRunCampaign = async (campaignId) => {
    setSaving(true);
    try {
      await runBonusCampaign(campaignId);
      setSnackbar({ open: true, message: 'Campaign disbursed successfully.', severity: 'success' });
      loadData();
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to run campaign.', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleFilterChange = (field) => (event) => {
    setFilters((prev) => ({ ...prev, [field]: event.target.value }));
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default', px: { xs: 1, md: 2 }, py: 3 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Bonus Management</Typography>
          <Typography variant="body2" color="text.secondary">
            Campaigns, bonus rules, manual controls, and audit trail.
          </Typography>
        </Box>
        <Button variant="outlined" onClick={loadData} disabled={loading}>Refresh</Button>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          ['Total Granted', summary?.total_granted],
          ['Total Used', summary?.total_used],
          ['Net Given Out', summary?.net_given_out],
          ['Outstanding Bonus', summary?.outstanding_bonus_balance],
        ].map(([label, value]) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={label}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary">{label}</Typography>
                <Typography variant="h6" fontWeight={700}>{formatMoney(value)}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700}>Usage Rule</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Maximum checkout bonus is calculated from order service fee profit.
              </Typography>
              <Stack direction="row" spacing={1}>
                <TextField
                  label="Profit Usage %"
                  type="number"
                  fullWidth
                  value={settings.usage_percentage}
                  onChange={(event) => setSettings({ usage_percentage: event.target.value })}
                  inputProps={{ min: 0, max: 100, step: '0.01' }}
                />
                <Button variant="contained" onClick={handleSaveSettings} disabled={saving}>Save</Button>
              </Stack>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700}>Manual Bonus</Typography>
              <Divider sx={{ my: 2 }} />
              <Stack spacing={2}>
                <TextField select label="User" value={form.user_id} onChange={(event) => setForm((prev) => ({ ...prev, user_id: event.target.value }))} fullWidth>
                  {userOptions.map((user) => <MenuItem key={user.id} value={user.id}>{user.label}</MenuItem>)}
                </TextField>
                <TextField select label="Action" value={form.action} onChange={(event) => setForm((prev) => ({ ...prev, action: event.target.value }))} fullWidth>
                  <MenuItem value="grant">Grant</MenuItem>
                  <MenuItem value="reverse">Reverse</MenuItem>
                </TextField>
                <TextField label="Amount" type="number" value={form.amount} onChange={(event) => setForm((prev) => ({ ...prev, amount: event.target.value }))} fullWidth />
                {form.action === 'grant' && (
                  <TextField
                    label="Expiry Date"
                    type="datetime-local"
                    value={form.expires_at}
                    onChange={(event) => setForm((prev) => ({ ...prev, expires_at: event.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                )}
                <TextField label="Reference" value={form.source_reference} onChange={(event) => setForm((prev) => ({ ...prev, source_reference: event.target.value }))} fullWidth />
                <TextField label="Reason" value={form.note} onChange={(event) => setForm((prev) => ({ ...prev, note: event.target.value }))} multiline minRows={3} fullWidth />
                <Button variant="contained" onClick={handleSubmit} disabled={saving}>Apply</Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 8 }}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700}>Campaign Builder</Typography>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField select label="Campaign Template" value={campaignForm.campaign_type} onChange={handleTemplateChange} fullWidth>
                    {templates.map((template) => (
                      <MenuItem key={template.campaign_type} value={template.campaign_type}>{template.name}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField label="Campaign Name" value={campaignForm.name} onChange={(event) => setCampaignForm((prev) => ({ ...prev, name: event.target.value }))} fullWidth />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField label="Amount per User" type="number" value={campaignForm.amount} onChange={(event) => setCampaignForm((prev) => ({ ...prev, amount: event.target.value }))} fullWidth />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField label="Expiry Days" type="number" value={campaignForm.expiry_days} onChange={(event) => setCampaignForm((prev) => ({ ...prev, expiry_days: event.target.value }))} fullWidth />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField select label="Audience" value={campaignForm.audience_type} onChange={(event) => setCampaignForm((prev) => ({ ...prev, audience_type: event.target.value }))} fullWidth>
                    <MenuItem value="all_users">All Users</MenuItem>
                    <MenuItem value="selected_users">Selected Users</MenuItem>
                    <MenuItem value="new_users">New Users</MenuItem>
                    <MenuItem value="dormant_users">Dormant Users</MenuItem>
                    <MenuItem value="high_value_customers">High-Value Customers</MenuItem>
                  </TextField>
                </Grid>
                {campaignForm.audience_type === 'selected_users' && (
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      select
                      SelectProps={{ multiple: true }}
                      label="Selected Users"
                      value={campaignForm.selected_users}
                      onChange={(event) => setCampaignForm((prev) => ({ ...prev, selected_users: event.target.value }))}
                      fullWidth
                    >
                      {userOptions.map((user) => <MenuItem key={user.id} value={user.id}>{user.label}</MenuItem>)}
                    </TextField>
                  </Grid>
                )}
                {campaignForm.audience_type === 'new_users' && (
                  <>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField type="date" label="Registered From" value={campaignForm.start_date} onChange={(event) => setCampaignForm((prev) => ({ ...prev, start_date: event.target.value }))} InputLabelProps={{ shrink: true }} fullWidth />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField type="date" label="Registered To" value={campaignForm.end_date} onChange={(event) => setCampaignForm((prev) => ({ ...prev, end_date: event.target.value }))} InputLabelProps={{ shrink: true }} fullWidth />
                    </Grid>
                  </>
                )}
                {campaignForm.audience_type === 'dormant_users' && (
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField label="Inactive Days" type="number" value={campaignForm.inactive_days} onChange={(event) => setCampaignForm((prev) => ({ ...prev, inactive_days: event.target.value }))} fullWidth />
                  </Grid>
                )}
                {campaignForm.audience_type === 'high_value_customers' && (
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField label="Min Paid Order Value" type="number" value={campaignForm.min_order_value} onChange={(event) => setCampaignForm((prev) => ({ ...prev, min_order_value: event.target.value }))} fullWidth />
                  </Grid>
                )}
                <Grid size={{ xs: 12 }}>
                  <TextField label="Reason" value={campaignForm.note} onChange={(event) => setCampaignForm((prev) => ({ ...prev, note: event.target.value }))} multiline minRows={2} fullWidth />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <Button variant="outlined" onClick={handlePreviewCampaign} disabled={saving}>Preview Audience</Button>
                    <Button variant="contained" onClick={handleCreateCampaign} disabled={saving}>Create Campaign</Button>
                  </Stack>
                </Grid>
                {campaignPreview && (
                  <Grid size={{ xs: 12 }}>
                    <Alert severity="info">
                      {campaignPreview.recipient_count} users will receive {formatMoney(campaignForm.amount)} each. Total: {formatMoney(campaignPreview.total_amount)}.
                    </Alert>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>

          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Campaigns</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Audience</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Recipients</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {campaigns.map((campaign) => (
                    <TableRow key={campaign.id} hover>
                      <TableCell>{campaign.name}</TableCell>
                      <TableCell>{campaign.audience_type}</TableCell>
                      <TableCell align="right">{formatMoney(campaign.amount)}</TableCell>
                      <TableCell><Chip size="small" label={campaign.status} /></TableCell>
                      <TableCell align="right">{campaign.total_recipients}</TableCell>
                      <TableCell align="right">
                        {['draft', 'scheduled'].includes(campaign.status) && (
                          <Button size="small" onClick={() => handleRunCampaign(campaign.id)} disabled={saving}>Run</Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {!campaigns.length && (
                    <TableRow><TableCell colSpan={6} align="center">No campaigns found.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
              <TextField label="Search Audit" value={filters.search} onChange={handleFilterChange('search')} fullWidth />
              <TextField select label="Action" value={filters.action} onChange={handleFilterChange('action')} sx={{ minWidth: 150 }}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="grant">Grant</MenuItem>
                <MenuItem value="apply">Apply</MenuItem>
                <MenuItem value="reverse">Reverse</MenuItem>
                <MenuItem value="expire">Expire</MenuItem>
              </TextField>
              <TextField type="date" label="Start" value={filters.start_date} onChange={handleFilterChange('start_date')} InputLabelProps={{ shrink: true }} />
              <TextField type="date" label="End" value={filters.end_date} onChange={handleFilterChange('end_date')} InputLabelProps={{ shrink: true }} />
            </Stack>

            {loading ? (
              <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress /></Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Campaign</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell align="right">Remaining</TableCell>
                      <TableCell>Reference</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ledger.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>{row.created_at ? new Date(row.created_at).toLocaleString() : '-'}</TableCell>
                        <TableCell>{row.user_display || row.user_email || row.user}</TableCell>
                        <TableCell><Chip size="small" label={row.action} color={actionColor[row.action] || 'default'} /></TableCell>
                        <TableCell>{row.campaign_name || '-'}</TableCell>
                        <TableCell align="right">{formatMoney(row.amount)}</TableCell>
                        <TableCell align="right">{formatMoney(row.remaining_after)}</TableCell>
                        <TableCell>{row.order_number || row.source_reference || row.transaction_reference || '-'}</TableCell>
                      </TableRow>
                    ))}
                    {!ledger.length && (
                      <TableRow><TableCell colSpan={7} align="center">No bonus activity found.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BonusManagement;
